import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Loader from '../components/Loader'
import { GameUploadCard, LandingUploadGate } from '../components/landing/GameUploadCard'
import supabase from '../supabaseClient'
import { COMMUNITY_SPORT_LABELS } from '../lib/communitySport'
import { ALL_COUNTRIES } from '../components/signup/countries'
import { getPublicGamePackages } from '../lib/communityCreditPricing'
import { getYoutubeLinkValidationReason } from '../components/academy-schedule/academySchedule.helpers'
import { uploadToS3 } from '../lib/awsS3UploadService'
import {
  clearQuickUploadIntent,
  clearQuickUploadOpen,
  communityNameTaken,
  patchQuickUploadIntent,
  readQuickUploadIntent,
  readQuickUploadUrl,
  rememberQuickUploadUrl,
  uploadQuickCommunityLogo,
  writeQuickUploadIntent,
} from '../lib/quickUpload'
import { AuthDialog } from '../components/landing/GameUploadCard'
import { signOutClientSession } from '../utils/authSignOut'
import {
  USERS_LOGIN_SELECT,
  profileCanAccessPortal,
  routePortalAfterAuth,
} from '../utils/signupFunnelRedirect'
import { readLastPortal } from '../utils/lastPortal'

function formatPlanPrice(value) {
  const amount = Number(value)
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2)
}

const SPORT_TONES = {
  Football: {
    selected: 'border-[#157347] bg-[#157347] text-white focus-visible:ring-[#157347]',
    idle: 'border-[#157347] bg-white text-[#157347] focus-visible:ring-[#157347]',
  },
  Basketball: {
    selected: 'border-[#C65312] bg-[#C65312] text-white focus-visible:ring-[#C65312]',
    idle: 'border-[#C65312] bg-white text-[#C65312] focus-visible:ring-[#C65312]',
  },
  Padel: {
    selected: 'border-[#1D4ED8] bg-[#1D4ED8] text-white focus-visible:ring-[#1D4ED8]',
    idle: 'border-[#1D4ED8] bg-white text-[#1D4ED8] focus-visible:ring-[#1D4ED8]',
  },
}
const COUNTRIES_ALPHA = [...ALL_COUNTRIES].sort((a, b) => a[0].localeCompare(b[0], 'en'))

function profileName(user) {
  return user?.user_metadata?.full_name || user?.user_metadata?.name || ''
}

function StepBack({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-sm text-sm font-semibold text-[#57534e] hover:text-[#1c1917] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917]"
    >
      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
        <path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Back
    </button>
  )
}

const FLOW_STEPS = [
  { id: 'details', label: 'Community' },
  { id: 'footage', label: 'Upload' },
  { id: 'plan', label: 'Payment' },
]

function FlowProgress({ step }) {
  const current = Math.max(0, FLOW_STEPS.findIndex((item) => item.id === step))
  const fill = FLOW_STEPS.length > 1 ? (current / (FLOW_STEPS.length - 1)) * 100 : 0
  return (
    <div className="relative mb-8">
      <div className="absolute left-[16.67%] right-[16.67%] top-3 h-px bg-[#e7e5e4]" aria-hidden="true">
        <span className="block h-full bg-[#071018]" style={{ width: `${fill}%` }} />
      </div>
      <ol className="relative grid grid-cols-3" aria-label="Progress">
        {FLOW_STEPS.map((item, index) => {
          const active = index === current
          const done = index < current
          return (
            <li key={item.id} className="flex flex-col items-center" aria-current={active ? 'step' : undefined}>
              <span
                className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${
                  done || active
                    ? 'bg-[#071018] text-white'
                    : 'border border-[#d6d3d1] bg-[#F4F6F8] text-[#a8a29e]'
                }`}
              >
                {done ? (
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                    <path d="M3.5 8.2 6.4 11l6.1-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  index + 1
                )}
              </span>
              <span className={`mt-2 text-center text-xs leading-none ${active ? 'font-semibold text-[#1c1917]' : done ? 'font-medium text-[#44403c]' : 'font-medium text-[#a8a29e]'}`}>
                {item.label}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default function QuickUpload({ onClose }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [sessionUser, setSessionUser] = useState(undefined)
  const [name, setName] = useState('')
  const [team, setTeam] = useState('')
  const [sport, setSport] = useState('Football')
  const [country, setCountry] = useState('')
  const [logoFile, setLogoFile] = useState(null)
  const [logoUrl, setLogoUrl] = useState('')
  const [logoPreview, setLogoPreview] = useState('')
  const [savedFileName, setSavedFileName] = useState('')
  const [source, setSource] = useState('video')
  const [youtubeUrl, setYoutubeUrl] = useState(() => readQuickUploadUrl())
  const [file, setFile] = useState(null)
  const [uploadStatus, setUploadStatus] = useState('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [videoUrl, setVideoUrl] = useState('')
  const [packageId, setPackageId] = useState('game_1')
  const planPackages = getPublicGamePackages()
  const [submitting, setSubmitting] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState(location.state?.oauthError || '')
  const [accountChecked, setAccountChecked] = useState(false)
  const [step, setStep] = useState('details')
  const sportRef = useRef(sport)
  const packageIdRef = useRef(packageId)
  sportRef.current = sport
  packageIdRef.current = packageId
  const sessionId = sessionUser === undefined ? undefined : sessionUser?.id || null

  const leaveToLanding = async () => {
    if (leaving) return
    setLeaving(true)
    clearQuickUploadIntent()
    clearQuickUploadOpen()
    onClose?.()
    try {
      await signOutClientSession()
    } catch (err) {
      console.error('[QuickUpload] sign out error:', err)
    }
    if (!onClose) navigate('/', { replace: true })
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      const user = data.session?.user || null
      setSessionUser(user)
      if (user) setName((current) => current || profileName(user))
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user || null
      setSessionUser(user)
      if (user) setName((current) => current || profileName(user))
    })
    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const intent = readQuickUploadIntent()
    if (!intent || intent.stage === 'ready') return
    if (intent.fullName) setName(intent.fullName)
    if (intent.teamName) setTeam(intent.teamName)
    if (intent.sport) setSport(intent.sport)
    if (intent.countryIso2) setCountry(intent.countryIso2)
    if (intent.logoUrl) {
      setLogoUrl(intent.logoUrl)
      setLogoPreview(intent.logoUrl)
    }
    if (intent.source === 'youtube' || intent.source === 'video') setSource(intent.source)
    if (intent.youtubeUrl) setYoutubeUrl(intent.youtubeUrl)
    if (intent.videoUrl) {
      setVideoUrl(intent.videoUrl)
      setUploadStatus('done')
      setUploadProgress(100)
    }
    if (intent.fileName) setSavedFileName(intent.fileName)
    if (intent.packageId) setPackageId(intent.packageId)
    if (intent.stage === 'pay') setStep('plan')
    else if (intent.videoUrl || intent.youtubeUrl) setStep('footage')
  }, [])

  useEffect(() => {
    if (!logoFile) return undefined
    const url = URL.createObjectURL(logoFile)
    setLogoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [logoFile])

  useEffect(() => {
    if (sessionId === undefined) return undefined
    if (!sessionId) {
      setAccountChecked(true)
      return undefined
    }

    let cancelled = false
    setAccountChecked(false)
    ;(async () => {
      const { data: profile, error: profileError } = await supabase
        .from('Users')
        .select(USERS_LOGIN_SELECT)
        .eq('user_id', sessionId)
        .maybeSingle()

      if (cancelled) return

      if (!profileError && profile) {
        if (profile.admin_Pass) {
          navigate('/admin/video-analysis', { replace: true })
          return
        }

        const lastPortal = readLastPortal()
        const portals = []
        if (lastPortal && profileCanAccessPortal(profile, lastPortal)) portals.push(lastPortal)
        for (const portal of ['community', 'academy', 'venue']) {
          if (portals.includes(portal)) continue
          if (profileCanAccessPortal(profile, portal)) portals.push(portal)
        }

        if (portals[0]) {
          const url = readQuickUploadUrl()
          if (portals[0] === 'community' && url && !getYoutubeLinkValidationReason(url)) {
            writeQuickUploadIntent({
              stage: 'ready',
              source: 'youtube',
              youtubeUrl: url,
              sport: sportRef.current,
              packageId: packageIdRef.current,
            })
          }
          const handled = await routePortalAfterAuth(navigate, profile, portals[0], {
            userId: sessionId,
          })
          if (handled || cancelled) return
        }
      }

      if (!cancelled) setAccountChecked(true)
    })()

    return () => {
      cancelled = true
    }
  }, [sessionId, navigate])

  const handleYoutube = (value) => {
    setYoutubeUrl(value)
    rememberQuickUploadUrl(value)
  }

  const handleFile = (next) => {
    setVideoUrl('')
    setSavedFileName(next?.name || '')
    setUploadProgress(0)
    setUploadStatus(next ? 'uploading' : 'idle')
    setError('')
    setFile(next)
    if (!next) patchQuickUploadIntent({ videoUrl: '', fileName: '' })
  }

  useEffect(() => {
    if (source !== 'video' || !file) return undefined
    const controller = new AbortController()
    let cancelled = false
    setUploadStatus('uploading')
    setUploadProgress(0)
    setVideoUrl('')
    uploadToS3(
      file,
      sessionId || 'quick-upload',
      (percent) => {
        if (!cancelled) setUploadProgress(Math.max(0, Math.min(100, Math.round(percent || 0))))
      },
      false,
      { signal: controller.signal, skipCompress: true },
    )
      .then((result) => {
        if (cancelled) return
        const url = result?.videoUrl || result?.publicUrl || ''
        if (!url) throw new Error('The video uploaded, but no link came back.')
        setVideoUrl(url)
        setSavedFileName(file.name)
        setUploadProgress(100)
        setUploadStatus('done')
        const current = readQuickUploadIntent()
        patchQuickUploadIntent({
          stage: current?.stage === 'pay' || current?.stage === 'ready' ? current.stage : 'draft',
          source: 'video',
          videoUrl: url,
          fileName: file.name,
          ...(name.trim() ? { fullName: name.trim() } : {}),
          ...(team.trim() ? { teamName: team.trim() } : {}),
          ...(sport ? { sport } : {}),
          ...(country ? { countryIso2: country } : {}),
        })
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return
        const message = err?.message || 'The upload failed. Choose the video again.'
        if (/abort/i.test(message)) return
        setUploadStatus('error')
        setError(message)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [file, source, sessionId])

  const footageReady = source === 'youtube'
    ? Boolean(String(youtubeUrl || '').trim()) && !getYoutubeLinkValidationReason(youtubeUrl)
    : uploadStatus === 'done' && Boolean(videoUrl)

  const continueFromDetails = async (event) => {
    event.preventDefault()
    setError('')
    if (!name.trim() || !team.trim()) {
      setError('Add your name and your community name.')
      return
    }
    if (!country) {
      setError('Choose a country.')
      return
    }
    const taken = await communityNameTaken(team)
    if (!taken.ok) {
      setError(taken.error)
      return
    }
    if (taken.taken) {
      setError('That community name is already taken. Choose another.')
      return
    }
    setStep('footage')
  }

  const continueToPlan = (event) => {
    event.preventDefault()
    setError('')
    if (source === 'youtube') {
      const reason = getYoutubeLinkValidationReason(youtubeUrl)
      if (reason) {
        setError(reason)
        return
      }
    } else if (!footageReady) {
      setError(uploadStatus === 'uploading' ? 'Wait until the video has uploaded.' : 'Upload the video before you continue.')
      return
    }
    setStep('plan')
  }

  const pay = async (event) => {
    event.preventDefault()
    if (submitting || step !== 'plan') return
    setError('')

    const selected = planPackages.find((pack) => pack.id === packageId) || planPackages[0]
    setSubmitting(true)
    const uploadedLogo = logoFile
      ? await uploadQuickCommunityLogo(sessionUser.id, logoFile)
      : { ok: true, url: logoUrl }
    if (!uploadedLogo.ok) {
      setError(uploadedLogo.error)
      setSubmitting(false)
      return
    }

    writeQuickUploadIntent({
      stage: 'pay',
      source,
      youtubeUrl: source === 'youtube' ? youtubeUrl.trim() : '',
      videoUrl: source === 'video' ? videoUrl : '',
      fileName: file?.name || savedFileName || readQuickUploadIntent()?.fileName || '',
      sport,
      packageId: selected.id,
      fullName: name.trim(),
      teamName: team.trim(),
      countryIso2: country,
      logoUrl: uploadedLogo.url || '',
    })
    navigate(`/upload/pay?pack=${encodeURIComponent(selected.id)}`)
  }

  if (sessionUser === undefined || (sessionUser && !accountChecked)) {
    return (
      <div className={onClose ? 'flex flex-1 items-center justify-center bg-[#F4F6F8] py-16' : 'flex min-h-[100svh] items-center justify-center bg-[#07080c]'}>
        <Loader size={32} />
      </div>
    )
  }

  if (!sessionUser && onClose) return null

  if (!sessionUser) {
    return (
      <div className="flex min-h-[100svh] flex-col bg-[#07080c] text-white">
        <header className="mx-auto flex w-full max-w-lg items-center justify-between px-5 pt-5">
          <Link to="/" className="flex items-center gap-2">
            <img src="/Logo_Statbox_Transparent.png" alt="" className="h-6 w-auto" />
            <img src="/logo_white.png" alt="Statbox" className="h-3 w-auto" />
          </Link>
          <Link to="/login" className="text-sm font-semibold text-white/80 hover:text-white">
            Log in
          </Link>
        </header>
        <main className="flex flex-1 flex-col items-center justify-center px-5 pb-16 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight">Sign up</h1>
          <p className="mt-3 max-w-sm text-base text-white/75">
            Then upload the game. One button, and you’re in.
          </p>
          <div className="mt-8">
            <LandingUploadGate error={error} />
          </div>
        </main>
      </div>
    )
  }

  const selectedPack = planPackages.find((pack) => pack.id === packageId) || planPackages[0]
  const fieldClass =
    'h-12 w-full rounded-2xl border border-[#e7e5e4] bg-white px-3 text-base text-[#1c1917] placeholder:text-[#78716c] focus:border-[#071018] focus:outline-none focus:ring-2 focus:ring-[#071018]/15'
  const payButtonClass =
    'flex min-h-[52px] w-full items-center justify-center rounded-full text-base font-bold tracking-[-0.02em] text-[#071018] shadow-[0_14px_32px_-12px_rgba(113,205,217,0.85),inset_0_1px_0_rgba(255,255,255,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8fe0ec] focus-visible:ring-offset-2 disabled:opacity-60'
  const payButtonStyle = { background: 'linear-gradient(180deg, #8fe0ec 0%, #71cdd9 58%, #5dbac6 100%)' }

  return (
    <div className={onClose ? 'flex min-h-0 flex-1 flex-col bg-[#F4F6F8] font-jakarta text-[#1c1917]' : 'min-h-[100svh] bg-[#F4F6F8] font-jakarta text-[#1c1917]'}>
      {onClose ? null : (
        <header className="z-50 shrink-0 bg-[#1E2A3C]">
          <div className="flex h-14 w-full items-center justify-start gap-2 px-4 sm:px-5">
            <button
              type="button"
              onClick={leaveToLanding}
              disabled={leaving}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
            >
              <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
                <path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>
            <img src="/logo_white.png" alt="Statbox" className="h-3 sm:h-3.5 w-auto" />
          </div>
        </header>
      )}

      <main className={onClose ? 'min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-7' : 'mx-auto w-full max-w-lg px-5 pb-16 pt-8'}>
        <form
          onSubmit={step === 'plan' ? pay : step === 'footage' ? continueToPlan : continueFromDetails}
        >
          <FlowProgress step={step} />
          {step === 'details' ? (
          <>
          <h1 id="quick-upload-title" className="text-[2rem] font-bold leading-tight tracking-tight">Quick start</h1>
          <p className="mt-2 max-w-[36ch] text-base leading-relaxed text-[#57534e]">
            Set up your community
          </p>

          <section className="mt-8">
            <div className="space-y-4">
              <div>
                <label htmlFor="upload-name" className="mb-1.5 block text-sm font-medium">
                  Your name
                </label>
                <input
                  id="upload-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                  required
                  className={fieldClass}
                />
              </div>
              <div>
                <label htmlFor="upload-team" className="mb-1.5 block text-sm font-medium">
                  Community name
                </label>
                <input
                  id="upload-team"
                  value={team}
                  onChange={(event) => setTeam(event.target.value)}
                  autoComplete="organization"
                  required
                  placeholder="Your community name"
                  className={fieldClass}
                />
              </div>
              <fieldset>
                <legend className="mb-1.5 text-sm font-medium">Sport</legend>
                <div className="grid grid-cols-3 gap-2">
                  {COMMUNITY_SPORT_LABELS.map((label) => {
                    const tone = SPORT_TONES[label] || SPORT_TONES.Football
                    const selected = sport === label
                    return (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setSport(label)}
                        className={`min-h-11 rounded-full border text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                          selected ? tone.selected : tone.idle
                        }`}
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
              </fieldset>
              <div>
                <label htmlFor="upload-country" className="mb-1.5 block text-sm font-medium">
                  Country
                </label>
                <select
                  id="upload-country"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  required
                  className={fieldClass}
                >
                  <option value="">Choose a country</option>
                  {COUNTRIES_ALPHA.map(([label, iso]) => (
                    <option key={iso} value={iso}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="upload-logo" className="mb-1.5 flex items-baseline gap-2 text-sm font-medium">
                  Community logo
                  <span className="text-xs font-normal text-[#78716c]">Optional</span>
                </label>
                <div className="flex items-center gap-3">
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="h-auto max-h-14 w-auto max-w-[10rem] rounded-xl object-contain" />
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-[#d6d3d1] bg-white text-xs font-semibold text-[#78716c]">
                      Logo
                    </span>
                  )}
                  <label className="inline-flex min-h-11 cursor-pointer items-center rounded-full border border-[#e7e5e4] bg-white px-4 text-sm font-semibold text-[#1c1917] hover:border-[#071018]">
                    {logoFile ? 'Change logo' : 'Choose image'}
                    <input
                      id="upload-logo"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(event) => {
                        const next = event.target.files?.[0] || null
                        if (next && !next.type.startsWith('image/')) {
                          setError('The community logo needs to be an image.')
                          event.target.value = ''
                          return
                        }
                        if (next && next.size > 5 * 1024 * 1024) {
                          setError('That logo is too large. Use an image under 5 MB.')
                          event.target.value = ''
                          return
                        }
                        setError('')
                        setLogoFile(next)
                        event.target.value = ''
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>

          {error ? (
            <p role="alert" className="mt-6 text-sm text-red-700">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className={`mt-8 ${payButtonClass}`}
            style={payButtonStyle}
          >
            Continue
          </button>
          </>
          ) : step === 'footage' ? (
          <>
          <StepBack onClick={() => { setError(''); setStep('details') }} />
          <h1 id="quick-upload-title" className="mt-4 text-[2rem] font-bold leading-tight tracking-tight">Footage</h1>
          <p className="mt-2 max-w-[36ch] text-base leading-relaxed text-[#57534e]">
            Add the video. You choose the plan next.
          </p>
          <section className="mt-8">
            <GameUploadCard
              source={source}
              onSourceChange={setSource}
              youtubeUrl={youtubeUrl}
              onYoutubeUrlChange={handleYoutube}
              fileName={file?.name || savedFileName}
              onFile={handleFile}
              uploadStatus={uploadStatus}
              uploadProgress={uploadProgress}
            />
          </section>

          {error ? (
            <p role="alert" className="mt-6 text-sm text-red-700">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={!footageReady}
            className={`mt-8 ${payButtonClass} disabled:opacity-50`}
            style={payButtonStyle}
          >
            {source === 'video' && uploadStatus === 'uploading' ? `Uploading ${uploadProgress}%` : 'Continue'}
          </button>
          </>
          ) : (
          <>
          <StepBack onClick={() => { setError(''); setStep('footage') }} />
          <h1 id="quick-upload-title" className="mt-4 text-[2rem] font-bold leading-tight tracking-tight">Choose your plan</h1>
          <p className="mt-2 max-w-[36ch] text-base leading-relaxed text-[#57534e]">
            Get your game stats and highlights. Pick the option that works for you.
          </p>

          <section className="mt-8">
            <div className="space-y-2" role="radiogroup" aria-label="Choose your plan">
              {planPackages.map((pack) => {
                const selected = packageId === pack.id
                const title = pack.credits === 1 ? 'Single game' : `${pack.credits}-game pack`
                const detail = pack.credits === 1
                  ? 'Full game stats and video highlights'
                  : `${pack.credits} games of stats and highlights · $${pack.perGame}/game`
                return (
                  <button
                    key={pack.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setPackageId(pack.id)}
                    className={`flex min-h-[72px] w-full items-center justify-between gap-4 rounded-2xl border-2 bg-white px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917] ${
                      selected ? 'border-[#1c1917]' : 'border-[#e7e5e4] hover:border-[#d6d3d1]'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-base font-semibold">{title}</span>
                      <span className="mt-0.5 block text-sm leading-snug text-[#57534e]">{detail}</span>
                      {pack.compareAt && pack.savings ? (
                        <span className="mt-1 block text-xs font-semibold text-[#0c3a42]">Save {pack.savings}% on your first game</span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-right">
                        {pack.compareAt ? (
                          <span className="block text-sm text-[#57534e] line-through tabular-nums">
                            ${Number(pack.compareAt).toFixed(2)}
                          </span>
                        ) : null}
                        <span className="block text-lg font-bold tabular-nums">
                          ${formatPlanPrice(pack.price)}
                        </span>
                      </span>
                      <span
                        aria-hidden
                        className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                          selected ? 'border-[#1c1917]' : 'border-[#d6d3d1]'
                        }`}
                      >
                        {selected ? <span className="h-2.5 w-2.5 rounded-full bg-[#1c1917]" /> : null}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </section>

          <div className="mt-8 border-t border-[#e7e5e4] pt-6">
            <div className="flex items-baseline justify-between">
              <span className="text-base font-medium">Total</span>
              <span className="text-3xl font-bold tabular-nums tracking-tight">${formatPlanPrice(selectedPack.price)}</span>
            </div>
            {error ? (
              <p role="alert" className="mt-4 text-sm text-red-700">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className={`mt-5 ${payButtonClass}`}
              style={payButtonStyle}
            >
              {submitting ? 'Opening payment…' : `Pay $${formatPlanPrice(selectedPack.price)}`}
            </button>
          </div>
          </>
          )}
        </form>
      </main>
    </div>
  )
}

export function QuickUploadDialog({ open, onClose }) {
  const [user, setUser] = useState(undefined)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    const apply = (session) => {
      if (!cancelled) setUser(session?.user || null)
    }
    supabase.auth.getSession().then(({ data }) => apply(data.session))
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => apply(session))
    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [open])

  useEffect(() => {
    if (!open || !user) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event) => {
      if (event.key === 'Escape') closeRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKey)
    }
  }, [open, user])

  if (!open) return null
  if (user === undefined) {
    return createPortal(
      <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/75">
        <Loader size={32} />
      </div>,
      document.body,
    )
  }
  if (!user) return <AuthDialog open onClose={onClose} />

  return createPortal(
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/75"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-upload-title"
        className="relative flex max-h-[min(920px,calc(100dvh-2rem))] w-full max-w-lg flex-col overflow-hidden rounded-[28px] bg-[#F4F6F8] text-[#1c1917] shadow-[0_28px_70px_-24px_rgba(0,0,0,0.65)]"
      >
        <QuickUpload onClose={onClose} />
      </div>
    </div>,
    document.body,
  )
}
